import { DsdFile } from "@/types/dsd";
import { BetaFeatureOnly } from "@/components/beta-feature";
import { ModalWrapper } from "@/components/modals/modal-wrapper";
import {
  TAGGING_EXCLUDED_LABEL,
  TAGGING_EXCLUDED_TOOLTIP,
  taggingExcludedNotice,
} from "@/lib/tagging-target";
import { cn } from "@/lib/utils";
import { Button } from "@/toggle-ui-kit/button";
import { CheckboxButton } from "@/toggle-ui-kit/control";
import { DecorateInteraction } from "@/toggle-ui-kit/decorate/interaction/decorate-interaction";
import { ScrollbarArea } from "@/toggle-ui-kit/scrollbar";
import { Tooltip } from "@/toggle-ui-kit/tooltip";
import { ArrowUpRight, ChevronRight, Loader2 } from "lucide-react";
import { ReactNode } from "react";
import {
  ForwardDisplayState,
  STATE_CHIP_CLASS,
  STATE_LABELS,
} from "./constants";
import {
  ForwardModalDescription,
  ForwardModalFooter,
  ForwardModalHeader,
} from "./forward-modal-chrome";
import { ForwardReportSummary } from "./forward-report-summary";

/** 목록 한 줄이 화면에 보여줄 값. 데이터 조회는 상위(컨테이너)가 책임진다. */
export interface ForwardTableRow {
  tableId: string;
  tableName: string;
  state: ForwardDisplayState;
  /** 진행 중(PENDING/RUNNING)이면 다시 고를 수 없다. */
  running: boolean;
  /** false 면 일반사항·회계정책·추정 등 목차의 표 — 목록엔 남되 "블록태깅" 칩을 달고 고를 수 없다. */
  taggingTarget: boolean;
}

export interface TableSelectViewProps {
  /** 이월 원본 보고서. 아직 못 찾았으면 요약 카드를 그리지 않는다. */
  sourceFile?: DsdFile;
  isLoading?: boolean;
  /**
   * 줄은 나왔는데(매칭 정보) 표별 진행 상태를 아직 못 받은 구간. 이때 상태 칩 자리는
   * 스피너로 둔다 — 상태를 모르는 것과 "아직 안 돌린 표(대기)"는 다르고,
   * 이미 끝난 표까지 잠깐 대기로 보였다가 뒤늦게 완료·건너뜀으로 바뀐다.
   */
  isStatusLoading?: boolean;
  rows: ForwardTableRow[];
  selectedIds: Set<string>;
  allSelected: boolean;
  selectedCount: number;
  isSelectAllDisabled?: boolean;
  onToggleRow: (tableId: string) => void;
  onToggleAll: () => void;
  expandedId: string | null;
  onToggleExpand: (tableId: string) => void;
  /** 펼친 줄에 붙는 상세(미리보기·수정) 영역. */
  renderExpanded?: (row: ForwardTableRow) => ReactNode;
  /**
   * 줄의 "이 표로 이동" 바로가기. 이동할 수 없는 줄이면 undefined 를 돌려 버튼을 감춘다
   * — 어느 줄로 이동할 수 있는지는 라우팅 정보를 아는 컨테이너만 판단할 수 있다.
   */
  resolveGoToTable?: (row: ForwardTableRow) => (() => void) | undefined;
  smartRollForward: boolean;
  onSmartRollForwardChange: (checked: boolean) => void;
  onPrev?: () => void;
  onStart: () => void;
  isStarting?: boolean;
}

/**
 * 표 한 줄 — Figma 5611:341511. 44px(p-12 + 20px 컨트롤).
 * Figma 의 stroke 는 안쪽으로 그려져 크기를 늘리지 않으므로 border 대신 inset ring 을 쓴다.
 */
const TableRow = ({
  row,
  checked,
  expanded,
  isStatusLoading,
  onToggleCheck,
  onToggleExpand,
  onGoToTable,
  children,
}: {
  row: ForwardTableRow;
  checked: boolean;
  expanded: boolean;
  isStatusLoading: boolean;
  onToggleCheck: () => void;
  onToggleExpand: () => void;
  onGoToTable?: () => void;
  children?: ReactNode;
}) => (
  <div className="rounded-[8px] bg-static-white ring-1 ring-inset ring-line-normal">
    <div
      className="group flex cursor-pointer items-center gap-2 p-3"
      onClick={onToggleExpand}
    >
      <span className="flex items-center" onClick={(e) => e.stopPropagation()}>
        <CheckboxButton
          checked={checked}
          disabled={row.running || !row.taggingTarget}
          onChange={onToggleCheck}
          aria-label={`${row.tableName} 선택`}
        />
      </span>
      <ChevronRight
        strokeWidth={1.8}
        className={cn(
          "size-5 shrink-0 text-label-normal transition-transform",
          expanded && "rotate-90",
        )}
      />
      <span
        className="label-1-normal-medium min-w-0 shrink truncate text-label-normal"
        title={row.tableName}
      >
        {row.tableName}
      </span>
      {onGoToTable && (
        <Tooltip label="이 표로 이동" side="top">
          <DecorateInteraction asChild variant="normal" color="neutral">
            <button
              type="button"
              onClick={(e) => {
                // 줄 클릭은 아코디언 토글이라 전파를 막는다.
                e.stopPropagation();
                onGoToTable();
              }}
              aria-label={`${row.tableName} 표로 이동`}
              className="flex size-5 shrink-0 items-center justify-center rounded-[6px] text-label-alternative opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
            >
              <ArrowUpRight strokeWidth={1.8} className="size-4" />
            </button>
          </DecorateInteraction>
        </Tooltip>
      )}
      {!row.taggingTarget ? (
        // 제외 표는 실행 상태가 없으니 상태 로딩과 무관하게 바로 "블록태깅" 칩을 둔다.
        <Tooltip label={TAGGING_EXCLUDED_TOOLTIP} side="top">
          <span
            className={cn(
              "ml-auto flex h-5 shrink-0 items-center justify-center gap-0.5 rounded-full px-1 py-0.5 caption-1-medium",
              STATE_CHIP_CLASS.NOT_STARTED,
            )}
          >
            {TAGGING_EXCLUDED_LABEL}
          </span>
        </Tooltip>
      ) : isStatusLoading ? (
        // 칩과 같은 자리·같은 높이 — 상태가 도착해도 줄이 밀리지 않는다.
        <span className="ml-auto flex h-5 shrink-0 items-center">
          <span className="sr-only">상태 불러오는 중</span>
          <Loader2
            aria-hidden
            strokeWidth={1.8}
            className="size-4 animate-spin text-label-alternative"
          />
        </span>
      ) : (
        <span
          className={cn(
            "ml-auto flex h-5 shrink-0 items-center justify-center gap-0.5 rounded-full px-1 py-0.5 caption-1-medium",
            STATE_CHIP_CLASS[row.state],
          )}
        >
          {row.running && <Loader2 className="size-3 animate-spin" />}
          {STATE_LABELS[row.state]}
        </span>
      )}
    </div>
    {expanded && children}
  </div>
);

/**
 * XBRL 태그 불러오기 2단계 — 이월할 표 고르기. Figma 5611:340977 / 5611:341478 / 5611:341733.
 * 모달 높이(800px)를 고정하고 본문만 스크롤하며, "전체 선택" 줄은 위에 붙어 있는다.
 */
export const TableSelectView = ({
  sourceFile,
  isLoading = false,
  isStatusLoading = false,
  rows,
  selectedIds,
  allSelected,
  selectedCount,
  isSelectAllDisabled = false,
  onToggleRow,
  onToggleAll,
  expandedId,
  onToggleExpand,
  renderExpanded,
  resolveGoToTable,
  smartRollForward,
  onSmartRollForwardChange,
  onPrev,
  onStart,
  isStarting = false,
}: TableSelectViewProps) => {
  // 한 번이라도 불러오기가 시작되면 원본 파일을 다시 고를 수 없다 —
  // 되돌아가 다른 파일로 바꾸면 어느 파일에서 불러온 태그인지 알 수 없어진다.
  // running 은 진행 중일 때만 참이라 완료 직후 풀린다. 이미 태그를 덮어쓴 뒤가 오히려
  // 가장 위험하므로, 시작 여부가 그대로 남는 state 로 판단한다.
  const hasStarted =
    isStarting || rows.some((row) => row.state !== "NOT_STARTED");
  // 일반사항·회계정책·추정 등 목차의 표는 목록엔 남되 불러오기에서 빠진다 — 머리말에 몇 개가 왜 빠지는지 적는다.
  const excludedCount = rows.filter((row) => !row.taggingTarget).length;

  return (
    <ModalWrapper className="flex h-[800px] max-h-[calc(100vh-48px)] w-[600px] max-w-[calc(100vw-48px)] flex-col gap-0 overflow-hidden rounded-[8px] border-0 bg-background-base p-0 shadow-xxlarge ring-1 ring-inset ring-line-normal">
      <ForwardModalHeader title="XBRL 태그 불러오기" />
      <ForwardModalDescription>
        이전 보고서의 태깅 정보를 덮어씁니다.
        {excludedCount > 0 && (
          <>
            <br />
            {taggingExcludedNotice(excludedCount)}
          </>
        )}
      </ForwardModalDescription>

      <ScrollbarArea
        className="min-h-0 w-full flex-1"
        viewportClassName="[&>div]:!block"
      >
        <div className="flex flex-col gap-2 px-6">
          {sourceFile && <ForwardReportSummary file={sourceFile} />}
          {isLoading ? (
            <div className="flex items-center justify-center gap-2 py-10 caption-1-regular text-label-alternative">
              <Loader2 className="size-4 animate-spin" />
              매칭 중입니다...
            </div>
          ) : rows.length > 0 ? (
            <div className="flex flex-col gap-2">
              <div className="sticky top-0 z-10 flex items-center justify-between bg-background-base">
                <label className="flex cursor-pointer items-center gap-2 caption-1-regular text-label-alternative">
                  <CheckboxButton
                    checked={allSelected}
                    disabled={isSelectAllDisabled}
                    onChange={onToggleAll}
                    aria-label="전체 선택"
                  />
                  전체 선택
                </label>
                <span className="caption-1-regular text-label-alternative">
                  {selectedCount}개 선택됨
                </span>
              </div>
              <div className="flex flex-col gap-1">
                {rows.map((row) => (
                  <TableRow
                    key={row.tableId}
                    row={row}
                    checked={selectedIds.has(row.tableId)}
                    expanded={expandedId === row.tableId}
                    isStatusLoading={isStatusLoading}
                    onToggleCheck={() => onToggleRow(row.tableId)}
                    onToggleExpand={() => onToggleExpand(row.tableId)}
                    onGoToTable={resolveGoToTable?.(row)}
                  >
                    {renderExpanded?.(row)}
                  </TableRow>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-[8px] bg-background-neutral-alternative p-4 caption-1-regular text-label-alternative ring-1 ring-inset ring-line-normal">
              불러올 수 있는 표가 없습니다.
            </div>
          )}
        </div>
      </ScrollbarArea>

      <ForwardModalFooter>
        <BetaFeatureOnly>
          <label
            className="mr-auto flex cursor-pointer select-none items-center gap-2"
            title="태그를 이월한 뒤, 태깅이 부족한 표만 AI가 직전 기를 참고해 마저 태깅합니다."
          >
            <CheckboxButton
              checked={smartRollForward}
              onChange={onSmartRollForwardChange}
              aria-label="스마트 롤포워드"
            />
            <span className="caption-1-regular text-label-alternative">
              스마트 롤포워드
            </span>
          </label>
        </BetaFeatureOnly>
        {onPrev && (
          <Button
            variant="outlined"
            color="secondary"
            size="base"
            disabled={hasStarted}
            onClick={onPrev}
          >
            이전
          </Button>
        )}
        <Button
          variant="solid"
          color="primary"
          size="base"
          leftIcon={isStarting && <Loader2 className="size-4 animate-spin" />}
          disabled={selectedCount === 0 || isStarting}
          onClick={onStart}
        >
          불러오기 시작
        </Button>
      </ForwardModalFooter>
    </ModalWrapper>
  );
};
